// don't use createeffect if you're not using createaction for the action
// do not mix the old (defining actions as classes) with the new (createeffect)
// you end up with bad things happening (double calls, or no calls at all)

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    filter,
    map,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { AccountService } from '../../account/account.service';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromRoot from '../../store';
import { AuthClassLookupService } from '../auth-class-lookup.service';
import { ColorService } from '../color.service';
import { ContractTypeLookupService } from '../contract-types-lookup.service';
import { DaysOfWeekService } from '../days-of-week.service';
import { DirectReportsLookupService } from '../direct-reports-lookup.service';
import { EntityFilterService } from '../entity-filter.service';
import { IndirectLaborTypesLookupService } from '../indirect-labor-types-lookup.service';
import { IUserGroup } from '../IUserGroup';
import { MessageDeliveryTypeService } from '../message-delivery-type.service';
import { MobileProviderService } from '../mobile-provider.service';
import { PayrollFrequencyService } from '../payroll-frequency.service';
import { PayrollItemLookupService } from '../payroll-item-lookup.service';
import { ServiceItemLookupService } from '../service-item-lookup.service';
import { SplitDayService } from '../split-day.service';
import { StateLookupService } from '../state-lookup.service';
import { TasksLookupService } from '../tasks-lookup.service';
import { TimeZoneService } from '../time-zone.service';
import { UserAuthorizationTypeLookupService } from '../user-authorization-type-lookup.service';
import { UserGroupLookupService } from '../user-group-lookup.service';
import { UserIdFormatService } from '../user-id-format.service';
import * as actions from './actions';
import * as fromLookups from './reducers';

@Injectable()
export class LookupEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromRoot.State>,
        private log: LoggingService,
        private userAuthTypeService: UserAuthorizationTypeLookupService,
        private drService: DirectReportsLookupService,
        private tasksService: TasksLookupService,
        private siService: ServiceItemLookupService,
        private ugService: UserGroupLookupService,
        private authClassService: AuthClassLookupService,
        private payService: PayrollItemLookupService,
        private stateService: StateLookupService,
        private contractTypeService: ContractTypeLookupService, // can't load?
        private entityFilterService: EntityFilterService,
        private indirectTypeService: IndirectLaborTypesLookupService, // can't load?
        private splitdayService: SplitDayService,
        private payrollFrequencyService: PayrollFrequencyService,
        private userIdFormatService: UserIdFormatService,
        private daysOfWeekService: DaysOfWeekService,
        private timeZoneService: TimeZoneService,
        private colorService: ColorService,
        private mobileProviderService: MobileProviderService,
        private accountService: AccountService,
        private messageDeliveryTypeService: MessageDeliveryTypeService
    ) {}

    LoadColors$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadColors),
            switchMap(() =>
                this.colorService.get().pipe(
                    map(data => new actions.SetColors(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    TimeZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadTimeZones),
            switchMap(() =>
                this.timeZoneService.get().pipe(
                    filter(data => !!data),
                    map(data => new actions.SetTimeZones(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    UserTimeZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadUserTimeZones),
            switchMap(() =>
                this.timeZoneService.getUserTimeZones().pipe(
                    filter(data => !!data),
                    map(data => new actions.SetUserTimeZones(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    DaysOfWeek$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadDaysOfWeek),
            switchMap(() =>
                this.daysOfWeekService.get().pipe(
                    tap(x => this.log.verbose({ type: 'DaysOfWeek$', x })),
                    map(data => new actions.SetDaysOfWeek(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadSplitdays$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadSplitday>(
                actions.LookupActionTypes.LoadSplitday
            ),
            tap(x => this.log.verbose({ type: 'LoadSplitdays$', x })),
            switchMap(() =>
                this.splitdayService.loadSplitdays().pipe(
                    tap(x =>
                        this.log.verbose({ type: 'LoadSplitdays$:resp', x })
                    ),
                    map(x => new actions.SetSplitday(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadUserAuthorizationTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadAuthorizationTypes),
            switchMap(_ =>
                this.userAuthTypeService.getAuthorizationTypes().pipe(
                    map(auths => new actions.SetAuthorizationTypes(auths)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    DirectReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadDirectReports>(
                actions.LookupActionTypes.LoadDirectReports
            ),
            switchMap(action =>
                this.drService
                    .get(
                        action.payload.user.DefaultRoleGroupID || 1,
                        action.payload.activeOnly,
                        action.payload.processId,
                        action.payload.MyDRsOnly
                    )
                    .pipe(
                        map(drs => new actions.SetDirectReports(drs)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );

    Tasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadTasks),
            switchMap(() =>
                this.tasksService.get().pipe(
                    map(tasks => new actions.SetTasks(tasks)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadUserTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadUserTasks>(
                actions.LookupActionTypes.LoadUserTasks
            ),
            switchMap(a =>
                this.tasksService
                    .getUserTasks(a.payload.ProcessID, a.payload.AsOfDate)
                    .pipe(
                        map(tasks => new actions.SetUserTasks(tasks)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    LoadContractTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadContractTasks>(
                actions.LookupActionTypes.LoadContractTasks
            ),
            switchMap(a =>
                this.tasksService
                    .getContractTasks(a.payload.contractlistid)
                    .pipe(
                        map(tasks => new actions.SetContractTasks(tasks)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    ServiceItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadServiceItems>(
                actions.LookupActionTypes.LoadServiceItems
            ),
            switchMap(a => {
                if (a.IncludeOtherChargeItem || a.IncludeSystem) {
                    return this.siService
                        .post(
                            a.activeOnly,
                            a.IncludeSystem,
                            a.IncludeOtherChargeItem
                        )
                        .pipe(
                            map(si => new actions.SetServiceItems(si)),
                            catchError(fromShared.Errors.handleServiceError)
                        );
                } else {
                    return this.siService.get(a.activeOnly).pipe(
                        map(si => new actions.SetServiceItems(si)),
                        catchError(fromShared.Errors.handleServiceError)
                    );
                }
            })
        )
    );

    LoadUserGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadUserGroups),
            switchMap(() =>
                this.ugService.get().pipe(
                    map(x => new actions.SetUserGroups(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    AuthorizationClasses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.LookupActionTypes.LoadAuthorizationClasses),
            switchMap(_ =>
                this.authClassService.get().pipe(
                    map(x => new actions.SetAuthorizationClasses(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    PayrollItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadPayrollItems>(
                actions.LookupActionTypes.LoadPayrollItems
            ),
            switchMap(a =>
                this.payService.get(a.payload).pipe(
                    map(x => new actions.SetPayrollItems(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    States$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadStates>(actions.LookupActionTypes.LoadStates),
            switchMap(a =>
                this.stateService.get().pipe(
                    map(x => new actions.SetStates(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadContractTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadContractTypes>(
                actions.LookupActionTypes.LoadContractTypes
            ),
            switchMap(a =>
                this.contractTypeService.getContractTypes().pipe(
                    map(x => new actions.SetContractTypes(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadIndirectTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadIndirectTypes>(
                actions.LookupActionTypes.LoadIndirectTypes
            ),
            switchMap(a =>
                this.indirectTypeService.getIndirectTypes().pipe(
                    map(x => new actions.SetIndirectTypes(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadEntityFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadEntityFilter>(
                actions.LookupActionTypes.LoadEntityFilter
            ),
            switchMap(a =>
                this.entityFilterService.loadEntityFilter(a.filterEntity).pipe(
                    tap(x =>
                        this.log.verbose({ type: 'LoadEntityFilter$', x })
                    ),
                    map(x => new actions.SetEntityFilter(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadPayrollFrequency$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadPayrollFrequency>(
                actions.LookupActionTypes.LoadPayrollFrequency
            ),
            withLatestFrom(
                this.store.pipe(select(fromLookups.getPayrollFrequency))
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.payrollFrequencyService.get().pipe(
                    map(x => new actions.SetPayrollFrequency(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadUserIdFormats$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadUserIdFormats>(
                actions.LookupActionTypes.LoadUserIdFormats
            ),
            withLatestFrom(
                this.store.pipe(select(fromLookups.getUserIdFormats))
            ),
            filter(([, items]) => items.length === 0),
            switchMap(a =>
                this.userIdFormatService.get().pipe(
                    map(x => new actions.SetUserIdFormats(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadMobileProviders$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.LoadMobileProviders>(
                actions.LookupActionTypes.LoadMobileProviders
            ),
            withLatestFrom(
                this.store.pipe(select(fromLookups.selectMobileProviders))
            ),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.mobileProviderService.get().pipe(
                    map(x => new actions.SetMobileProviders(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    GetCreditCards$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.GetCreditCards>(
                actions.LookupActionTypes.GetCreditCards
            ),
            withLatestFrom(this.store.pipe(select(fromLookups.getCreditCards))),
            filter(([, items]) => !items || items.length === 0),
            switchMap(([action]) =>
                this.accountService.get(action.payload).pipe(
                    map(data => new actions.SetCreditCards(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    GetMessageDeliveryTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<actions.GetMessageDeliveryTypes>(
                actions.LookupActionTypes.GetMessageDeliveryTypes
            ),
            withLatestFrom(
                this.store.pipe(select(fromLookups.selectMessageDeliveryTypes))
            ),
            filter(([, items]) => !items || items.length === 0),
            switchMap(([action]) =>
                this.messageDeliveryTypeService.get().pipe(
                    map(data => new actions.SetMessageDeliveryTypes(data)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
